import { useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useStore, useAuthStore } from '../store'
import { createWebSocket, resolveAvatarUrl } from '../api'
import { Avatar, Beacon, Button } from '../ui'
import { useCriticalTickets } from '../hooks/useCriticalTickets'
import ProfileModal from './ProfileModal'
import { SPLIT_PAGES } from './SplitView'
import {
  HiShieldCheck, HiSquares2X2, HiTicket, HiUsers, HiPresentationChartLine,
  HiCircleStack, HiMap, HiCpuChip, HiDocumentText, HiQuestionMarkCircle,
  HiCog6Tooth, HiSparkles, HiSwatch, HiCheckCircle,
  HiMagnifyingGlass, HiBell, HiSun, HiMoon, HiArrowRightOnRectangle, HiXMark,
  HiEnvelope, HiChatBubbleLeftRight, HiChevronLeft, HiChevronRight,
  HiEllipsisHorizontal, HiInbox, HiVideoCamera, HiStar, HiUserGroup,
} from 'react-icons/hi2'

// Toast ko'rinishi: ikonka + kit ohangi. Kitda uchta ohang bor (critical / ok / info),
// do'kondagi turlar shularga solishtiriladi — rang faqat signal tashiydi (DESIGN.md 1).
const toastConfig = {
  critical: { Icon: Beacon,        tone: 'critical' },
  ok:       { Icon: HiCheckCircle, tone: 'ok' },
  ticket:   { Icon: HiTicket,      tone: 'info' },
  message:  { Icon: HiEnvelope,    tone: 'ok' },
  default:  { Icon: HiBell,        tone: 'info' },
}

// Bildirishnoma qatoridagi ikonka — emoji o'rniga hi2 (DESIGN.md 8),
// kritikda esa mayoq: konsolda diqqat belgisi bitta
const notifIcon = { critical: Beacon, message: HiChatBubbleLeftRight }

const buildNav = (isSuperAdmin, hasPermission = () => false) => [
  { section: 'Asosiy' },
  { id: 'dashboard',     label: 'Bosh sahifa',         Icon: HiSquares2X2,            path: '/' },
  { id: 'tickets',       label: 'Murojaatlar',         Icon: HiInbox,                 path: '/tickets' },
  { section: 'Bilim bazasi' },
  { id: 'templates',     label: 'Shablonlar',          Icon: HiDocumentText,          path: '/templates' },
  { id: 'faq',           label: 'Resurslar',           Icon: HiQuestionMarkCircle,    path: '/faq',        perm: 'faq' },
  { id: 'channel',       label: 'Kanal bazasi',        Icon: HiCircleStack,           path: '/channel',    adminOnly: true },
  { section: 'Tahlil' },
  { id: 'reports',       label: 'Hisobotlar',          Icon: HiPresentationChartLine, path: '/reports' },
  { id: 'feedback',      label: 'Fikrlar',             Icon: HiStar,                  path: '/feedback' },
  { id: 'map',           label: 'Xarita',              Icon: HiMap,                   path: '/map' },
  { section: 'Boshqaruv' },
  { id: 'users',         label: 'Foydalanuvchilar',    Icon: HiUserGroup,             path: '/users',      adminOnly: true },
  { id: 'operators',     label: 'Operatorlar',         Icon: HiUsers,                 path: '/operators',  adminOnly: true },
  { id: 'ai-monitoring', label: 'AI kuzatuvi',        Icon: HiSparkles,              path: '/ai-monitoring', adminOnly: true },
  { id: 'resource-gaps', label: 'Video bo\'shliqlari', Icon: HiVideoCamera,           path: '/resource-gaps', adminOnly: true },
  { id: 'server',        label: 'Server & SSL',        Icon: HiCpuChip,               path: '/server',     adminOnly: true },
  { id: 'settings',      label: 'Sozlamalar',          Icon: HiCog6Tooth,             path: '/settings',   perm: 'settings' },
  // Ro'yxat oxirida alohida: kitning jonli katalogi (DESIGN.md 11) — faqat super admin
  { id: 'uikit',         label: 'Dizayn tizimi',       Icon: HiSwatch,                path: '/ui',         adminOnly: true },
].filter(i => {
  if (i.section) return true
  if (i.adminOnly) return isSuperAdmin
  if (i.perm) return isSuperAdmin || hasPermission(i.perm)
  return true
})

// ── BILDIRISHNOMA POPOVERI ─────────────────────────────────
// Yig'ilgan va yoyilgan panelda bir xil ro'yxat ko'rsatiladi, faqat joylashuvi farq qiladi.
function NotifPopover({ notifs, style, popRef, onMarkAll, onSelect }) {
  return (
    <div className="notif-pop" ref={popRef} style={style}>
      <div className="notif-pop-hdr">
        <span>Bildirishnomalar</span>
        {notifs.length > 0 && (
          <button onClick={onMarkAll}>Barchasini o'qish</button>
        )}
      </div>
      <div className="notif-pop-body">
        {notifs.length === 0 ? (
          <div className="notif-empty"><HiEnvelope /> Bildirishnoma yo'q</div>
        ) : notifs.map(n => {
          const Icon = notifIcon[n.type] || HiEnvelope
          return (
            <div key={n.id} className={`notif-row ${n.unread ? 'unread' : ''}`}
                 onClick={() => onSelect(n)}>
              <span className={`notif-ico ${n.type}`}><Icon /></span>
              <div className="notif-txt">
                <div>{n.text}</div>
                <small>{n.time}</small>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── KRITIK PANEL ───────────────────────────────────────────
// Mayoq (`searching` orbi) — konsoldagi yagona signal harakati
// (DESIGN.md 7). Fon miltillashi yo'q: eski critPulse olib tashlangan,
// yangi kritik kelganda ro'yxat o'zi ochiladi, mayoq esa doim aylanib turadi.
function CriticalPanel({ collapsed }) {
  const navigate = useNavigate()
  const { clearSosAlert } = useStore()
  // So'rov va hisoblagichlar umumiy hook'da: telefondagi pastki qator ham
  // xuddi shu ro'yxatni ko'rsatadi, ikkalasi bitta manbadan o'qiydi.
  const { tickets, extra, pending, total } = useCriticalTickets()
  const [open, setOpen] = useState(false)
  const prevCount       = useRef(0)

  useEffect(() => {
    if (pending > prevCount.current) setOpen(true)
    prevCount.current = pending
  }, [pending])

  if (total === 0) return null

  if (collapsed) {
    return (
      <div className="sb-crit-mini">
        <button className="nav-item crit-mini has"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                aria-label={`Kritik murojaatlar: ${pending || total}`}>
          <Beacon label={false} />
          {pending > 0 && <span className="crit-dot">{pending > 9 ? '9+' : pending}</span>}
        </button>
      </div>
    )
  }

  return (
    <div className="sb-crit">
      <button className="crit-btn has"
              aria-expanded={open}
              onClick={() => setOpen(o => !o)}>
        <Beacon size="sm" label={false} />
        <span className="crit-label">Kritik</span>
        {total > 0 && <span className="crit-count">{pending || total}</span>}
      </button>

      {open && total > 0 && (
        <div className="crit-list">
          {tickets.map(t => (
            <div key={t.id} className="crit-row" onClick={() => navigate(`/ticket/${t.id}`)}>
              <div className="crit-row-main">
                <span className="crit-num">#{t.ticket_number}</span>
                {t.description && <span className="crit-desc">{t.description}</span>}
              </div>
              <span className={`crit-chip ${t.status === 'new' ? 'new' : ''}`}>
                {t.status === 'new' ? 'Yangi' : t.status === 'accepted' ? 'Qabul' : 'Jarayon'}
              </span>
            </div>
          ))}
          {extra.map(a => (
            <div key={a.id} className="crit-row" onClick={() => navigate('/tickets?priority=critical')}>
              <div className="crit-row-main">
                <span className="crit-num">#{a.ticket_number}</span>
                {a.body && <span className="crit-desc">{a.body}</span>}
              </div>
              <button className="crit-x" aria-label="Ogohlantirishni o'chirish"
                      onClick={e => { e.stopPropagation(); clearSosAlert(a.id) }}>
                <HiXMark />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── ASOSIY SIDEBAR ─────────────────────────────────────────
export default function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, theme, toggleTheme,
          mobileNavOpen, setMobileNav,
          setSplitPage, toasts, addToast, removeToast } = useStore()
  const { user, logout, isSuperAdmin, hasPermission } = useAuthStore()
  const navigate     = useNavigate()
  const { pathname } = useLocation()
  const nav          = buildNav(isSuperAdmin(), hasPermission)

  const [q, setQ]                 = useState('')
  const [notifs, setNotifs]       = useState([])
  const [unread, setUnread]       = useState(0)
  const [notifOpen, setNotifOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [menuOpen, setMenuOpen]   = useState(false)
  const wsRef  = useRef(null)
  const popRef = useRef(null)
  const menuRef = useRef(null)

  // Heartbeat App.jsx da (30s) yuboriladi — bu yerdagi dublikat interval olib tashlandi

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (window.Notification.permission === 'default') {
        window.Notification.requestPermission().catch(err => console.error(err))
      }
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    wsRef.current = createWebSocket(user.id, ev => {
      console.log('[WS]', ev)
      const push = (text, type, extra = {}) => {
        setNotifs(p => [{ id: Date.now() + Math.random(), text, type, time: 'Hozirgina', unread: true, ...extra }, ...p.slice(0, 19)])
        setUnread(n => n + 1)
        addToast(text, type)

        if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'granted') {
          try {
            new window.Notification("JKX CRT System", {
              body: text,
              icon: '/favicon.ico',
            })
          } catch (e) {
            console.error('System Notification error:', e)
          }
        }
      }
      const { addNewTicket: addNT, addSosAlert: addSos, triggerCriticalReload: trigReload, setLiveMapEvent } = useStore.getState()
      if (ev.type === 'new_ticket') {
        const msgText = `${ev.citizen || 'Fuqaro'}: ${ev.body || 'Yangi murojaat'}`
        const liveEv = {
          ticket_number: ev.ticket_number,
          citizen: ev.citizen || 'Fuqaro',
          body: ev.body || 'Yangi murojaat',
          is_critical: ev.is_critical,
          latitude: ev.latitude,
          longitude: ev.longitude,
          tuman: ev.tuman,
          id: ev.ticket_id || null,
          ts: Date.now(),
        }
        push(msgText, ev.is_critical ? 'critical' : 'ticket', { liveEv })
        addNT({ ...liveEv, time: new Date().toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' }) })
        setLiveMapEvent(liveEv)
        trigReload()
        if (ev.is_critical) {
          addSos({
            id: Date.now(), ticket_number: ev.ticket_number, citizen: ev.citizen, body: ev.body,
            time: new Date().toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' }),
          })
        }
      } else if (ev.type === 'new_message') {
        const msgText = `${ev.citizen || 'Fuqaro'}: ${ev.body || 'Yangi xabar'}`
        const liveEv = {
          ticket_number: ev.ticket_number,
          citizen: ev.citizen || 'Fuqaro',
          body: ev.body || 'Yangi xabar',
          is_critical: ev.is_critical,
          latitude: ev.latitude,
          longitude: ev.longitude,
          tuman: ev.tuman,
          id: ev.ticket_id || null,
          isMessage: true,
          ts: Date.now(),
        }
        push(msgText, ev.is_critical ? 'critical' : 'message', { liveEv })
        addNT({ ...liveEv, time: new Date().toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' }) })
        setLiveMapEvent(liveEv)
        trigReload()
      } else if (ev.type === 'assigned_tickets') {
        push(ev.message || `${ev.count} ta murojaat tayinlandi`, 'ticket')
      }
    })
    return () => { wsRef.current?.close(); wsRef.current = null }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  useEffect(() => {
    const h = e => {
      if (popRef.current && !popRef.current.contains(e.target)) setNotifOpen(false)
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const doSearch = e => {
    if (e.key === 'Enter' && q.trim()) { navigate('/tickets?search=' + encodeURIComponent(q.trim())); setQ('') }
  }

  const markAllRead = () => { setNotifs(p => p.map(n => ({ ...n, unread: false }))); setUnread(0) }

  const openNotif = n => {
    setNotifOpen(false)
    setMenuOpen(false)
    if (n.liveEv) {
      useStore.getState().setLiveMapEvent(n.liveEv)
      navigate('/map')
    } else {
      navigate('/tickets')
    }
  }

  const avatarSrc = resolveAvatarUrl(user?.avatar_url)

  return (
    <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileNavOpen ? 'is-open' : ''}`}>

      {/* Yopish — faqat tor ekranda (CSS). Ortdagi qoraytirish va Esc ham
          yopadi, lekin barmoq uchun ko'rinadigan tugma kerak. */}
      <button className="sb-close" onClick={() => setMobileNav(false)}
              aria-label="Menyuni yopish">
        <HiXMark size={18} />
      </button>

      <div className="sb-logo" onClick={() => navigate('/')}>
        <div className="sb-logo-icon">
          <svg viewBox="0 0 1024 1024" fill="currentColor">
            <path d="M512.61 766.47c-191.73 0-382.35 23.79-446.14-114.4-21.3-46.15 156.87 53.84 302.12 65.37C269.82 673.22 142.26 612.89 111 502.09 97.46 454 325 655.19 382.14 669.37c-42.61-38.46-223.06-198.32-168.49-294.18 25.18-44.22 184 242.27 184 242.27s-134.75-248.84-58.1-324.95c31-30.76 67.78 276.88 141.38 344.17 0 0-35.83-382.63 30-380.71s51.32 244.19 32 384.55C630 519.4 645.53 315.59 672.64 288.67s69.72 153.82-63.91 332.64c77.47-13.46 197.54-299.95 203.35-251.88 19 157.26-166.55 294.18-166.55 294.18 120.07-38.46 244-188.43 269.19-165.36s-112.33 186.51-292.43 225c211.1-1.92 338.27-127.59 337-84.6-3.78 125.01-254.95 127.82-446.68 127.82z" />
          </svg>
        </div>
        <div className="sb-logo-text">
          <div className="sb-logo-title">Cyber Advice</div>
        </div>
      </div>

      <div className="sb-search">
        <HiMagnifyingGlass className="sb-search-ico" />
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={doSearch}
               aria-label="Ticket qidirish"
               placeholder="Ticket qidirish…" spellCheck={false} />
      </div>

      <div className="sb-nav">
        {nav.map((item, i) => {
          if (item.section) return <div key={i} className="sb-section">{item.section}</div>
          const active = item.path === '/' ? pathname === '/' : pathname.startsWith(item.path)
          const { Icon } = item
          const hasSplit = !!SPLIT_PAGES[item.id]
          return (
            <div key={item.id} className="nav-item-container">
              <button
                className={`nav-item ${active ? 'active' : ''}`}
                aria-current={active ? 'page' : undefined}
                draggable
                onDragStart={e => {
                  e.dataTransfer.setData('application/x-page', item.id)
                  e.dataTransfer.effectAllowed = 'copy'
                }}
                onClick={() => navigate(item.path)}
                title={sidebarCollapsed ? item.label : "Bosing — ochish · O'ngga sudrang — 2 panel"}
              >
                <Icon className="nav-icon-svg" />
                <span className="nav-label">{item.label}</span>
              </button>
              {!sidebarCollapsed && hasSplit && (
                <button
                  className="nav-split-btn"
                  title="O'ng panelda ochish"
                  aria-label={`${item.label}: o'ng panelda ochish`}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    setSplitPage(item.id)
                  }}
                >
                  <HiChevronRight />
                </button>
              )}
              {item.id === 'tickets' && <CriticalPanel collapsed={sidebarCollapsed} />}
            </div>
          )
        })}
      </div>

      <button className="collapse-trigger" onClick={toggleSidebar}
              title="Panelni yig'ish"
              aria-label={sidebarCollapsed ? 'Panelni yoyish' : "Panelni yig'ish"}>
        {sidebarCollapsed ? <HiChevronRight /> : <HiChevronLeft />}
      </button>

      <div className="sb-footer">
        <div className="sb-footer-row" ref={menuRef}>
          {sidebarCollapsed ? (
            <button className="sb-circle-btn" style={{ margin: '0 auto' }}
                    onClick={() => setMenuOpen(o => !o)}
                    title="Menyu" aria-label="Foydalanuvchi menyusi"
                    aria-haspopup="menu" aria-expanded={menuOpen}>
              <Avatar name={user?.full_name} src={avatarSrc} size={28} />
            </button>
          ) : (
            <>
              {/* Chap tomon: avatar va ism — profil oynasini ochadi */}
              <Button variant="ghost" className="ui-grow sb-user-btn" style={{ justifyContent: 'flex-start' }}
                      title={user?.full_name || 'Profil'}
                      onClick={() => { setProfileOpen(true); setMenuOpen(false) }}>
                <Avatar name={user?.full_name} src={avatarSrc} size={28} />
                <span className="sb-user-name">
                  {user?.full_name || 'Foydalanuvchi'}
                </span>
              </Button>

              {/* O'ng tomon: menyu va bildirishnoma tugmalari */}
              <div className="ui-row ui-gap-8">
                <button className="sb-circle-btn" onClick={() => setMenuOpen(o => !o)}
                        title="Kengaytirilgan menyu" aria-label="Kengaytirilgan menyu"
                        aria-haspopup="menu" aria-expanded={menuOpen}>
                  <HiEllipsisHorizontal size={16} />
                </button>

                <div style={{ position: 'relative' }}>
                  <button className="sb-circle-btn"
                          onClick={() => { setNotifOpen(o => !o); setUnread(0) }}
                          title="Bildirishnomalar"
                          aria-label={unread > 0 ? `Bildirishnomalar: ${unread} ta yangi` : 'Bildirishnomalar'}
                          aria-expanded={notifOpen}>
                    <HiBell size={16} />
                    {unread > 0 && <span className="sb-bell-dot" />}
                  </button>
                  {notifOpen && (
                    <NotifPopover
                      notifs={notifs}
                      popRef={popRef}
                      style={{ bottom: '100%', right: 0, marginBottom: 'var(--ui-sp-8)', width: 280 }}
                      onMarkAll={markAllRead}
                      onSelect={openNotif}
                    />
                  )}
                </div>
              </div>
            </>
          )}

          {menuOpen && (
            // DIQQAT: bu yerga `ui-menu` sinfini QO'SHMANG. U `top: calc(100% + 4px)`
            // beradi, bu panel esa `bottom: 0` bilan yuqoriga ochiladi — ikkisi birga
            // turganda blokning balandligi manfiy bo'lib, menyu ko'rinmay qoladi.
            // Ko'rinish uchun kerak bo'lgan hamma narsa `.sb-menu-dropdown` da bor;
            // bolalar esa `ui-menu__item` ni ishlatadi — ular to'qnashmaydi.
            <div className="sb-menu-dropdown" role="menu">
              {sidebarCollapsed && (
                <div style={{ position: 'relative', width: '100%' }}>
                  <button className="ui-menu__item" role="menuitem"
                          onClick={() => { setNotifOpen(o => !o); setUnread(0) }}>
                    <HiBell className="sb-menu-item-icon" />
                    <span>Bildirishnomalar</span>
                    {unread > 0 && <span className="sb-menu-badge">{unread > 9 ? '9+' : unread}</span>}
                  </button>

                  {notifOpen && (
                    <NotifPopover
                      notifs={notifs}
                      popRef={popRef}
                      style={{ bottom: '100%', left: 0, width: 280 }}
                      onMarkAll={markAllRead}
                      onSelect={openNotif}
                    />
                  )}
                </div>
              )}

              <button className="ui-menu__item" role="menuitem"
                      onClick={() => { setMenuOpen(false); navigate('/profile') }}>
                <HiShieldCheck className="sb-menu-item-icon" />
                <span>Profil va 2FA</span>
              </button>

              <button className="ui-menu__item" role="menuitem"
                      onClick={() => { toggleTheme(); setMenuOpen(false) }}>
                {theme === 'light' ? <HiMoon className="sb-menu-item-icon" /> : <HiSun className="sb-menu-item-icon" />}
                <span>Mavzu: {theme === 'light' ? 'Tungi' : 'Kunduzgi'}</span>
              </button>

              <div className="ui-menu__sep" role="separator" />

              <button className="ui-menu__item ui-menu__item--danger" role="menuitem" onClick={logout}>
                <HiArrowRightOnRectangle className="sb-menu-item-icon" />
                <span>Chiqish</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} />}

      {/* Toast: holat do'kondan keladi, ko'rinish — kitning ui-toast sinflari.
          DIQQAT — nima uchun portal: tor ekranda yon panelning o'zi
          `transform` bilan chetga suriladi, `transform` esa ichidagi
          `position: fixed` bolalar uchun YANGI o'lchov nuqtasi yaratadi.
          Ya'ni panel yopiq turganda xabarlar u bilan birga ekrandan chiqib
          ketardi. Portal ularni to'g'ridan-to'g'ri `body` ga chiqaradi —
          xabar panelning holatiga umuman bog'liq bo'lmaydi. */}
      {createPortal(
        <div className={`toast-container ui-toast-region ${sidebarCollapsed ? 'is-sb-collapsed' : ''}`}>
          {toasts.map(t => {
            const cfg = toastConfig[t.type] || toastConfig.default
            return (
              <div key={t.id}
                   className={`ui-toast ui-toast--${cfg.tone}`}
                   role={cfg.tone === 'critical' ? 'alert' : 'status'}
                   aria-live={cfg.tone === 'critical' ? 'assertive' : 'polite'}>
                <cfg.Icon size={16} aria-hidden="true" />
                <span className="ui-grow">{t.text}</span>
                <Button variant="ghost" size="sm" icon={HiXMark} iconOnly
                        aria-label="Bildirishnomani yopish"
                        onClick={() => removeToast(t.id)} />
              </div>
            )
          })}
        </div>,
        document.body
      )}
    </aside>
  )
}
